import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  acceptConsent,
  rejectConsent,
  hasConsentChoice,
} from '../lib/consent';

/**
 * Cookie consent banner — shown on first visit, hidden after a choice is made.
 *
 * Design intent:
 *   - Bottom-right slide-in card. Doesn't block page content.
 *   - Two clear actions: Accept All (primary indigo) + Reject Non-Essential.
 *   - Links to Privacy Policy. No dark patterns.
 *   - On reset (footer link "Cookie preferences"), the banner reappears.
 *
 * The actual gtag('consent', 'update', ...) calls live in lib/consent.js.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Wait until the user has had a chance to read the hero. Mobile especially
    // benefits from this delay — the banner is a meaningful chunk of screen
    // real estate, so showing it instantly hurts first-impression and bounce
    // rate. 4s lets a typical visitor parse the headline + first CTA first.
    const t = setTimeout(() => {
      if (!hasConsentChoice()) setVisible(true);
    }, 4000);
    // Allow footer "Cookie preferences" link to re-open the banner via
    // a custom event from ConsentReset link.
    const handler = () => setVisible(true);
    window.addEventListener('shipmaster:open-cookie-banner', handler);
    return () => {
      clearTimeout(t);
      window.removeEventListener('shipmaster:open-cookie-banner', handler);
    };
  }, []);

  if (!visible) return null;

  const handleAccept = () => {
    acceptConsent();
    setVisible(false);
  };
  const handleReject = () => {
    rejectConsent();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[60] animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="relative rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-slate-700/50 p-3.5 sm:p-6">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-indigo-500/20 flex items-center justify-center mt-0.5">
            <Cookie className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-300" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm sm:text-base leading-tight">
              We use cookies
            </h3>
            {/* Full copy on tablet+ only — keeps mobile slim. */}
            <p className="hidden sm:block mt-2 text-sm text-slate-300 leading-relaxed">
              We use essential cookies to run the site and optional analytics
              cookies to understand how visitors use it. You can accept all or
              keep only essentials. Read our{' '}
              <Link
                to="/privacy"
                className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
              >
                privacy policy
              </Link>
              .
            </p>
            {/* Compact mobile copy */}
            <p className="sm:hidden mt-1 text-xs text-slate-400 leading-snug">
              Essential + optional analytics.{' '}
              <Link
                to="/privacy"
                className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
              >
                Privacy
              </Link>
            </p>
          </div>
          <button
            type="button"
            onClick={handleReject}
            aria-label="Close and reject non-essential cookies"
            data-testid="cookie-banner-close"
            className="flex-shrink-0 -mr-1 -mt-1 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-3 sm:mt-5 flex flex-row gap-2">
          <button
            type="button"
            onClick={handleReject}
            data-testid="cookie-banner-reject"
            className="flex-1 h-9 sm:h-10 rounded-full text-xs sm:text-sm font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors"
          >
            Essentials only
          </button>
          <button
            type="button"
            onClick={handleAccept}
            data-testid="cookie-banner-accept"
            className="flex-1 h-9 sm:h-10 rounded-full text-xs sm:text-sm font-bold bg-indigo-500 hover:bg-indigo-400 text-white transition-colors shadow-md"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
